import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { MdArrowForward, MdContentCopy } from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { selectPromoById } from "../../store/slices/promoSlice";
import { usePromoItemScreen } from "./usePromoItemScreen";
import { useCopyToClipboard } from "../../common/hooks/useCopyToClipboard";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { Banner } from "../main/components/banner/Banner";
import styles from "./PromoItemScreen.module.scss";
import { Share } from "../../common/components/share/Share";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";

export default function PromoItemScreen() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const { copy } = useCopyToClipboard();
  const [showToast, setShowToast] = useState(false);
  const { getCategoryName, getStoreName, getSaleItemName } =
    usePromoItemScreen();
  const item = useSelector(selectPromoById(Number(id)));

  const handleCopy = async (code: string) => {
    await copy(code);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 1500);
  };

  if (!item) {
    return (
      <div
        className={styles.container}
        style={{ backgroundColor: theme.background }}
      >
        <PageMeta customTitle="Акция не найдена" />
        <ProductHeader
          title="Акция не найдена"
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
          rightContent={null}
        />
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta
        itemName={item?.name}
        itemDescription={item?.description}
        itemImageFile={item?.files?.[0]}
        itemId={item?.id}
        type="article"
      />
      <ProductHeader
        title={item.name}
        isFavorite={false}
        showFavorite={false}
        textColor={theme.text}
        borderColor={theme.border}
        rightContent={<Share item={item} />}
      />

      <div className={styles.bannerWrapper}>
        <Banner items={[item]} variantProp={"base"} noText />
      </div>

      <div className={styles.content}>
        <h1 className={styles.name}>{item.name}</h1>

        {item.description && (
          <div className={styles.descriptionWrapper}>
            <p className={styles.description}>{item.description}</p>
          </div>
        )}

        <div className={styles.promoParams}>
          {/* Скидка за самовывоз */}
          {item.forPickup && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Скидка за самовывоз</h4>
              <span className={styles.chipText}>
                Скидка {item.discount}% при самовывозе
                {item.minSum && ` (от ${item.minSum} ₽)`}
              </span>
              {item.storesFor?.length > 0 && (
                <div className={styles.chipsRow}>
                  {item.storesFor.map((el: number) => (
                    <span key={el} className={styles.chipText}>
                      {getStoreName(el)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Подарок за сумму */}
          {item.giftBySum && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Подарок за сумму</h4>
              <span className={styles.chipText}>
                При заказе от {item.minSum} ₽ — выберите один товар в подарок:
              </span>
              {item.giftItemIds && item.giftItemIds.length > 0 && (
                <span className={styles.chipText}>
                  {item.giftItemIds
                    .map((el: number) => getSaleItemName(el))
                    .join(", ")}
                </span>
              )}
            </div>
          )}

          {item.itemsFor?.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {item.forFirstPurchase
                  ? "На первую покупку товаров"
                  : "На покупку товаров"}
              </h4>
              <div className={styles.chipsRow}>
                {item.itemsFor.map((el) => (
                  <span key={el} className={styles.chipText}>
                    {getSaleItemName(el)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.categoriesFor?.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {item.forFirstPurchase
                  ? "На первую покупку в категориях"
                  : "На покупку в категориях"}
              </h4>
              <div className={styles.chipsRow}>
                {item.categoriesFor.map((el) => (
                  <span key={el} className={styles.chipText}>
                    {getCategoryName(el)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.storesFor?.length > 0 && !item.forPickup && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {item.forFirstPurchase ? "На первую покупку в" : "На покупку в"}
              </h4>
              <div className={styles.chipsRow}>
                {item.storesFor.map((el: number) => (
                  <span key={el} className={styles.chipText}>
                    {getStoreName(el)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className={styles.rowGroup}>
            {item.discountCode && (
              <div className={styles.promoCodeContainer}>
                <h4 className={styles.sectionTitle}>Промокод</h4>
                <button
                  className={styles.promoCodeButton}
                  onClick={() => handleCopy(item.discountCode!)}
                >
                  <span className={styles.promoCode}>{item.discountCode}</span>
                  <MdContentCopy size={16} />
                </button>
                <p className={styles.copyHint}>Нажмите, чтобы скопировать</p>
              </div>
            )}

            {item.datesImportant && (item.startDate || item.endDate) && (
              <div className={styles.datesContainer}>
                {item.startDate && (
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>Начало</span>
                    <span className={styles.dateValue}>
                      {format(new Date(item.startDate), "dd.MM HH:mm", {
                        locale: ru,
                      })}
                    </span>
                  </div>
                )}
                {item.startDate && item.endDate && <MdArrowForward size={16} />}
                {item.endDate && (
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>Окончание</span>
                    <span className={styles.dateValue}>
                      {format(new Date(item.endDate), "dd.MM HH:mm", {
                        locale: ru,
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showToast && <div className={styles.copyToast}>СКОПИРОВАНО</div>}
    </div>
  );
}
